import { createSlice } from '@reduxjs/toolkit';

// Any room whose name contains "bath" is treated as a bath area — the BathType step
// is injected per-room and controls the visibility of ShowerFloor / ShowerWall
// inside THAT room (not globally).
function isBathRoom(room) {
    return /bath/i.test(room || '');
}

// Compute which steps are visible given the current per-area meta.
// baseSteps already reflects the "add KitchenArrangement / BathType, drop bath Counters"
// synthesis performed in setActiveFloorplan.
function filterStepsByMeta(baseSteps, sceneMeta) {
    return baseSteps.filter(s => {
        if (!isBathRoom(s.room)) return true;
        if (s.name !== 'ShowerFloor' && s.name !== 'ShowerWall') return true;

        const roomMeta = (sceneMeta && sceneMeta[s.room]) || {};
        const hasChosen = Object.prototype.hasOwnProperty.call(roomMeta, 'showerHasFloor')
                       || Object.prototype.hasOwnProperty.call(roomMeta, 'showerHasWall');

        // Until the user picks a bath type for this room, keep both tile steps
        // visible so nothing disappears unexpectedly.
        if (!hasChosen) return true;

        if (s.name === 'ShowerFloor' && !roomMeta.showerHasFloor) return false;
        if (s.name === 'ShowerWall' && !roomMeta.showerHasWall) return false;
        return true;
    });
}

const initialState = {
    // Active floorplan and scene
    activeFloorplanId: null,
    activeScene: null,

    // Step navigation
    activeSteps: [],
    // Full, unfiltered step list synthesized for the active floorplan.
    // Used to re-derive activeSteps when gating conditions change (e.g. shower type).
    baseSteps: [],
    currentStepIndex: 0,

    // Product selections - keyed by surface name for stability
    stepSelections: {},
    // Example: { "Roof": { product, surfaceName, room, stepName, selectedAt, stepIndex } }

    // Sel strings per scene area (persisted selections per room)
    sceneSelStrings: {},
    // Example: { "Exterior": "123,456,789", "Kitchen": "111,222,333" }

    // Per-area scene specifier overrides (e.g., Kitchen -> alternate arrangement).
    // Populated by KitchenArrangement / ShowerType steps.
    sceneSpecifierOverrides: {},
    // Example: { "Kitchen": "Holland_Homes/Images/2026_Redo/Kitchen_C-1" }

    // Per-area sub-type meta (shower-type id, kitchen arrangement id).
    // Used to gate downstream steps (e.g., Shower Floor / Wall visibility).
    sceneMeta: {},
    // Example: { "Bathroom": { showerType: "fiberglass-tub-shower" } }

    // Track which step indices have been completed (array for Redux serializability)
    completedStepIndices: [],

    // Chameleon visualization state
    specifier: '',
    selString: '-1',
    surfaceList: [],

    // UI state
    showAppliedProducts: false,
    vizLoading: false,

    // Selection history for undo/redo
    selectionHistory: {
        past: [],
        future: []
    }
};

const visualizerSlice = createSlice({
    name: 'visualizer',
    initialState,
    reducers: {
        // Set active floorplan and load its steps
        setActiveFloorplan: (state, action) => {
            const { floorplanId, steps } = action.payload;
            state.activeFloorplanId = floorplanId;
            state.sceneSpecifierOverrides = {};
            state.sceneMeta = {};

            if (floorplanId != null && steps && steps.length > 0) {
                // Filter steps for this floorplan
                const planSteps = steps.filter(step => {
                    const stepPlanId = step.PlanId || step.planId;
                    return String(stepPlanId) === String(floorplanId);
                });

                // Parse params and organize steps
                const parsedSteps = planSteps.map(step => {
                    const params = {};
                    const paramsString = step.Params || step.params || '';

                    // Parse params string: "title:Stone,series:8487,surface:Roof"
                    if (paramsString) {
                        paramsString.split(',').forEach(param => {
                            const [key, value] = param.split(':').map(s => s.trim());
                            if (key && value) {
                                params[key] = value;
                            }
                        });
                    }

                    return {
                        name: step.Name || step.name,
                        planId: step.PlanId || step.planId,
                        room: step.Room || step.room,
                        params: params,
                        originalData: step
                    };
                });

                // Inject synthesized steps:
                // 1) KitchenArrangement as the first Kitchen step.
                // 2) BathType as the first step of every bath room (Primary Bath / Guest Bath / legacy Bathroom).
                // Also drop Counters in bath rooms (client feedback: carry over from Kitchen).
                const kitchenIdx = parsedSteps.findIndex(s => (s.room || '').toLowerCase() === 'kitchen');
                if (kitchenIdx !== -1) {
                    parsedSteps.splice(kitchenIdx, 0, {
                        name: 'KitchenArrangement',
                        planId: floorplanId,
                        room: 'Kitchen',
                        params: {},
                        originalData: null
                    });
                }

                const filtered = parsedSteps.filter(s => {
                    if (isBathRoom(s.room) && s.name === 'Counters') return false;
                    return true;
                });

                // Distinct bath rooms in order of first appearance.
                const bathRooms = [];
                filtered.forEach(s => {
                    if (isBathRoom(s.room) && !bathRooms.includes(s.room)) {
                        bathRooms.push(s.room);
                    }
                });

                // Per bath room: inject a ShowerFloor before ShowerWall (if missing)
                // and prepend a BathType step at the head of that room's steps.
                bathRooms.forEach(room => {
                    const wallIdx = filtered.findIndex(s => s.name === 'ShowerWall' && s.room === room);
                    const hasFloor = filtered.some(s => s.name === 'ShowerFloor' && s.room === room);
                    if (wallIdx !== -1 && !hasFloor) {
                        filtered.splice(wallIdx, 0, {
                            name: 'ShowerFloor',
                            planId: floorplanId,
                            room,
                            params: {},
                            originalData: null
                        });
                    }

                    const firstIdx = filtered.findIndex(s => s.room === room);
                    if (firstIdx !== -1) {
                        filtered.splice(firstIdx, 0, {
                            name: 'BathType',
                            planId: floorplanId,
                            room,
                            params: {},
                            originalData: null
                        });
                    }
                });

                state.baseSteps = filtered;
                state.activeSteps = filterStepsByMeta(filtered, state.sceneMeta);

                console.log('Active steps loaded:', state.activeSteps.length, 'steps for floorplan', floorplanId);
            } else {
                state.baseSteps = [];
                state.activeSteps = [];
            }

            // Reset to first step
            state.currentStepIndex = 0;
        },

        // Override the scene specifier for a specific area (Kitchen arrangement, Shower type).
        setSceneSpecifierOverride: (state, action) => {
            const { area, specifier } = action.payload;
            if (!area) return;
            if (specifier) {
                state.sceneSpecifierOverrides[area] = specifier;
            } else {
                delete state.sceneSpecifierOverrides[area];
            }
            // Reset the persisted sel for that room — the new scene starts fresh.
            delete state.sceneSelStrings[area];
            if (state.activeScene && state.activeScene.area === area) {
                state.activeScene = { ...state.activeScene, specifier, sel: '-1' };
            }
        },

        // Store per-area meta (bath type key, kitchen arrangement key).
        // For bath-type changes on any bath room, rebuild the visible activeSteps
        // so ShowerFloor / ShowerWall reflect what the chosen type supports.
        setSceneMeta: (state, action) => {
            const { area, meta } = action.payload;
            if (!area) return;
            state.sceneMeta[area] = { ...(state.sceneMeta[area] || {}), ...(meta || {}) };

            const bathTouched = isBathRoom(area) && meta && (
                Object.prototype.hasOwnProperty.call(meta, 'showerHasFloor') ||
                Object.prototype.hasOwnProperty.call(meta, 'showerHasWall')
            );

            if (bathTouched) {
                // Preserve the currently active step where possible when re-deriving.
                const currentStep = state.activeSteps[state.currentStepIndex];
                const nextSteps = filterStepsByMeta(state.baseSteps, state.sceneMeta);
                let nextIndex = 0;
                if (currentStep) {
                    const idx = nextSteps.findIndex(s => s.name === currentStep.name && s.room === currentStep.room);
                    if (idx !== -1) nextIndex = idx;
                }
                state.activeSteps = nextSteps;
                state.currentStepIndex = nextIndex;
            }
        },

        // Set active floorplan ID only (legacy helper)
        setActiveFloorplanId: (state, action) => {
            state.activeFloorplanId = action.payload;
        },

        // Set active scene
        setActiveScene: (state, action) => {
            const newScene = action.payload;

            // Before switching, save the current scene's sel if we have one
            // This ensures we don't lose changes when rapidly switching
            if (state.activeScene && state.activeScene.area && state.activeScene.sel) {
                state.sceneSelStrings[state.activeScene.area] = state.activeScene.sel;
            }

            state.activeScene = newScene;
        },

        // Update a specific scene's sel string by area (safer than updateActiveSceneSel)
        updateSceneSelString: (state, action) => {
            const { area, sel } = action.payload;
            if (area && sel) {
                state.sceneSelStrings[area] = sel;
                // Also update activeScene.sel if it matches the area
                if (state.activeScene && state.activeScene.area === area) {
                    state.activeScene.sel = sel;
                }
            }
        },

        // Update active scene's sel string (after product applied)
        // DEPRECATED: Use updateSceneSelString instead for explicit area targeting
        updateActiveSceneSel: (state, action) => {
            if (state.activeScene) {
                state.activeScene.sel = action.payload;
                // Also save to sceneSelStrings keyed by area
                const area = state.activeScene.area;
                if (area) {
                    state.sceneSelStrings[area] = action.payload;
                }
            }
        },

        // Navigate to next step
        nextStep: (state) => {
            if (state.currentStepIndex < state.activeSteps.length - 1) {
                state.currentStepIndex += 1;
            }
        },

        // Navigate to previous step
        previousStep: (state) => {
            if (state.currentStepIndex > 0) {
                state.currentStepIndex -= 1;
            }
        },

        // Jump to specific step index
        jumpToStep: (state, action) => {
            const index = action.payload;
            if (index >= 0 && index < state.activeSteps.length) {
                state.currentStepIndex = index;
            }
        },

        // Select a product for a surface
        selectProduct: (state, action) => {
            const { product, surfaceName, stepIndex, selectionKey, tierLabel } = action.payload;
            const currentStep = state.activeSteps[stepIndex];

            if (!currentStep) return;

            // Save current state to history for undo
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = []; // Clear redo history

            // Use provided selectionKey or generate from stepName_room
            const key = selectionKey || `${currentStep.name}_${currentStep.room}`;

            // For unified steps that used to store per-material sub-keys
            // (Floor_LVP_Kitchen, Counters_granite_Kitchen, ...), wipe any
            // legacy siblings for the same room so only the latest pick remains.
            const room = currentStep.room || '';
            const roomSuffix = `_${room}`;
            if (key.endsWith(roomSuffix)) {
                const stepBase = key.slice(0, key.length - roomSuffix.length);
                if (stepBase === 'Floor' || stepBase === 'Counters') {
                    Object.keys(state.stepSelections).forEach(k => {
                        if (k === key) return;
                        if (k.startsWith(`${stepBase}_`) && k.endsWith(roomSuffix)) {
                            delete state.stepSelections[k];
                        }
                    });
                }
            }

            // Store selection keyed by stepName_room
            state.stepSelections[key] = {
                product,
                surfaceName,
                room: currentStep.room,
                stepName: currentStep.name,
                stepParams: currentStep.params,
                selectedAt: new Date().toISOString(),
                stepIndex,
                selectionKey: key,
                tierLabel: tierLabel || ''
            };

            // Mark this step index as completed (avoid duplicates)
            if (!state.completedStepIndices.includes(stepIndex)) {
                state.completedStepIndices.push(stepIndex);
            }

            console.log('Product selected for', key, ':', product.color || product.name);
            console.log('Step', stepIndex, 'marked as completed. Total completed:', state.completedStepIndices.length);
        },

        // Clear a selection
        clearSelection: (state, action) => {
            const surfaceName = action.payload;

            // Save to history
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = [];

            delete state.stepSelections[surfaceName];
        },

        // Clear all selections
        clearAllSelections: (state) => {
            // Save to history
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = [];

            state.stepSelections = {};
        },

        // Undo last selection
        undoSelection: (state) => {
            if (state.selectionHistory.past.length > 0) {
                // Move current to future
                state.selectionHistory.future.unshift(JSON.parse(JSON.stringify(state.stepSelections)));

                // Restore previous state
                const previousState = state.selectionHistory.past.pop();
                state.stepSelections = previousState;
            }
        },

        // Redo selection
        redoSelection: (state) => {
            if (state.selectionHistory.future.length > 0) {
                // Move current to past
                state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));

                // Restore future state
                const futureState = state.selectionHistory.future.shift();
                state.stepSelections = futureState;
            }
        },

        // Toggle applied products view
        toggleAppliedProducts: (state, action) => {
            state.showAppliedProducts = action.payload !== undefined
                ? action.payload
                : !state.showAppliedProducts;
        },

        // Set Chameleon specifier
        setSpecifier: (state, action) => {
            state.specifier = action.payload;
        },

        // Set Chameleon sel string
        setSelString: (state, action) => {
            state.selString = action.payload;
        },

        // Set surface list from Chameleon
        setSurfaceList: (state, action) => {
            state.surfaceList = action.payload;
        },

        // Set viz loading state
        setVizLoading: (state, action) => {
            state.vizLoading = action.payload;
        },

        // Reset visualizer state
        resetVisualizer: (state) => {
            return initialState;
        }
    }
});

export const {
    setActiveFloorplan,
    setActiveFloorplanId,
    setActiveScene,
    updateSceneSelString,
    updateActiveSceneSel,
    setSceneSpecifierOverride,
    setSceneMeta,
    nextStep,
    previousStep,
    jumpToStep,
    selectProduct,
    clearSelection,
    clearAllSelections,
    undoSelection,
    redoSelection,
    toggleAppliedProducts,
    setSpecifier,
    setSelString,
    setSurfaceList,
    setVizLoading,
    resetVisualizer
} = visualizerSlice.actions;

// Selectors
export const selectCurrentStep = (state) => {
    const { activeSteps, currentStepIndex } = state.visualizer;
    return activeSteps[currentStepIndex] || null;
};

export const selectProgressPercentage = (state) => {
    const { completedStepIndices, activeSteps } = state.visualizer;
    const completedCount = completedStepIndices ? completedStepIndices.length : 0;
    const totalSteps = activeSteps.length;
    return totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
};

export const selectHasSelection = (surfaceName) => (state) => {
    return state.visualizer.stepSelections[surfaceName] !== undefined;
};

export const selectCanUndo = (state) => {
    return state.visualizer.selectionHistory.past.length > 0;
};

export const selectCanRedo = (state) => {
    return state.visualizer.selectionHistory.future.length > 0;
};

export default visualizerSlice.reducer;
